import React, { useState, useEffect, useCallback } from 'react';
import {
  Calendar, CheckCircle2, Eye, FileText, Link2, RefreshCw, Trash2, Wand2, XCircle,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AutoBlogTab() {
  const token = sessionStorage.getItem('admin_token');
  const [drafts, setDrafts] = useState([]);
  const [queue, setQueue] = useState(null);
  const [orphans, setOrphans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [actioning, setActioning] = useState('');
  const [preview, setPreview] = useState(null); // draft being viewed
  const [resultBanner, setResultBanner] = useState(null);
  const [faqStatus, setFaqStatus] = useState(null);
  const [citeStatus, setCiteStatus] = useState(null);

  const loadCiteStatus = useCallback(async () => {
    if (!token) return;
    try {
      const s = await fetch(`${API_URL}/api/admin/citations/backfill/status`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json());
      setCiteStatus(s);
    } catch {}
  }, [token]);

  const loadFaqStatus = useCallback(async () => {
    if (!token) return;
    try {
      const s = await fetch(`${API_URL}/api/admin/autoblog/backfill-faqs/status`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json());
      setFaqStatus(s);
    } catch {}
  }, [token]);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [d, q, o] = await Promise.all([
        fetch(`${API_URL}/api/admin/autoblog/drafts`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
        fetch(`${API_URL}/api/admin/autoblog/queue`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
        fetch(`${API_URL}/api/admin/autoblog/orphans?min_inbound=1`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
      ]);
      setDrafts(d.drafts || []);
      setQueue(q);
      setOrphans(o.orphans || []);
    } catch {} finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); loadFaqStatus(); loadCiteStatus(); }, [load, loadFaqStatus, loadCiteStatus]);

  // Poll citations backfill progress while it's running
  useEffect(() => {
    if (!citeStatus?.running) return;
    const t = setInterval(loadCiteStatus, 10000);
    return () => clearInterval(t);
  }, [citeStatus?.running, loadCiteStatus]);

  const startCitationsBackfill = async () => {
    if (!window.confirm('Find and verify the PubMed studies behind every science article (Peptides, Longevity, Biohacking, Recovery, Nutrition, Wellness)? Each citation is verified against the live PubMed API — real studies and real authors only. Runs in the background (~20-60s per article).')) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/citations/backfill`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) {
        setResultBanner({ kind: 'info', msg: 'Research citations backfill started — progress updates below every 10s.' });
      } else {
        setResultBanner({ kind: 'err', msg: `Citations backfill failed to start: ${data.detail || res.status}` });
      }
    } catch (e) {
      setResultBanner({ kind: 'err', msg: `Network: ${e.message}` });
    } finally {
      setTimeout(loadCiteStatus, 1500);
    }
  };

  // Poll FAQ backfill progress while it's running
  useEffect(() => {
    if (!faqStatus?.running) return;
    const t = setInterval(loadFaqStatus, 10000);
    return () => clearInterval(t);
  }, [faqStatus?.running, loadFaqStatus]);

  const startFaqBackfill = async () => {
    if (!window.confirm('Generate and append an SEO-optimized FAQ section to every published article that lacks one? This runs in the background (Claude, ~2-5s per article) and finishes with a single IndexNow batch ping.')) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/autoblog/backfill-faqs`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) {
        setResultBanner({ kind: 'info', msg: 'FAQ backfill started in the background — progress updates below every 10s.' });
      } else {
        setResultBanner({ kind: 'err', msg: `FAQ backfill failed to start: ${data.detail || res.status}` });
      }
    } catch (e) {
      setResultBanner({ kind: 'err', msg: `Network: ${e.message}` });
    } finally {
      setTimeout(loadFaqStatus, 1500);
    }
  };

  const runNow = async () => {
    setRunning(true);
    setResultBanner({ kind: 'info', msg: 'Generating new draft… Claude usually takes 60-120 seconds. The draft will appear below when ready.' });
    try {
      const res = await fetch(`${API_URL}/api/admin/autoblog/run`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setResultBanner({ kind: 'ok', msg: `Draft created: "${data.title}" (${data.category}).` });
      } else {
        const e = await res.json().catch(() => ({}));
        setResultBanner({ kind: 'err', msg: `Generation failed: ${e.detail || res.status}` });
      }
    } catch (e) {
      // Likely an ingress timeout — the draft probably still landed. Refresh and check.
      setResultBanner({ kind: 'info', msg: 'Long-running request — checking for the new draft anyway.' });
    } finally {
      setRunning(false);
      await load();
    }
  };

  const approve = async (slug) => {
    setActioning(`approve-${slug}`);
    try {
      const res = await fetch(`${API_URL}/api/admin/autoblog/drafts/${slug}/approve`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) {
        setResultBanner({
          kind: 'ok',
          msg: `Published "${slug}" · llms.txt: ${data.llms_txt_regenerated ? '✓' : '✗'} · sitemap: ${data.sitemap_regenerated ? '✓' : '✗'} · IndexNow: ${data.indexnow?.ok ? '✓' : '✗'} (${data.indexnow?.status})`,
        });
      } else {
        setResultBanner({ kind: 'err', msg: `Approve failed: ${data.detail || res.status}` });
      }
    } catch (e) {
      setResultBanner({ kind: 'err', msg: `Network: ${e.message}` });
    } finally {
      setActioning('');
      setPreview(null);
      await load();
    }
  };

  const reject = async (slug) => {
    if (!window.confirm(`Reject and permanently delete draft "${slug}"?`)) return;
    setActioning(`reject-${slug}`);
    try {
      await fetch(`${API_URL}/api/admin/autoblog/drafts/${slug}/reject`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      setResultBanner({ kind: 'ok', msg: `Rejected draft "${slug}".` });
    } catch {} finally {
      setActioning('');
      setPreview(null);
      await load();
    }
  };

  const approveAll = async () => {
    if (drafts.length === 0) return;
    if (!window.confirm(`Approve and publish ALL ${drafts.length} pending drafts? llms.txt + sitemap will regenerate once, and IndexNow will ping all URLs in a single batch.`)) return;
    setActioning('approve-all');
    setResultBanner({ kind: 'info', msg: `Publishing ${drafts.length} drafts in a single batch…` });
    try {
      const res = await fetch(`${API_URL}/api/admin/autoblog/drafts/approve-all`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) {
        setResultBanner({
          kind: 'ok',
          msg: `Published ${data.approved_count} articles in 1 batch · llms.txt: ${data.llms_txt_regenerated ? '✓' : '✗'} · sitemap: ${data.sitemap_regenerated ? '✓' : '✗'} · IndexNow: ${data.indexnow?.ok ? '✓' : '✗'} (${data.indexnow?.status}, ${data.indexnow?.count} URLs)`,
        });
      } else {
        setResultBanner({ kind: 'err', msg: `Bulk approve failed: ${data.detail || res.status}` });
      }
    } catch (e) {
      setResultBanner({ kind: 'err', msg: `Network: ${e.message}` });
    } finally {
      setActioning('');
      await load();
    }
  };

  const rejectAll = async () => {
    if (drafts.length === 0) return;
    if (!window.confirm(`Permanently delete ALL ${drafts.length} pending drafts? This cannot be undone.`)) return;
    setActioning('reject-all');
    try {
      const res = await fetch(`${API_URL}/api/admin/autoblog/drafts/reject-all`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setResultBanner({ kind: 'ok', msg: `Deleted ${data.deleted_count} draft(s).` });
    } catch (e) {
      setResultBanner({ kind: 'err', msg: `Network: ${e.message}` });
    } finally {
      setActioning('');
      await load();
    }
  };

  if (loading) return <div className="text-center py-12 text-gray-400" data-testid="autoblog-loading">Loading Auto-Blog…</div>;

  return (
    <div className="space-y-6" data-testid="autoblog-tab">
      {/* Result banner */}
      {resultBanner && (
        <div className={`p-3 rounded-lg text-xs flex items-center justify-between ${
          resultBanner.kind === 'ok' ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' :
          resultBanner.kind === 'err' ? 'bg-red-50 text-red-800 border border-red-200' :
          'bg-blue-50 text-blue-800 border border-blue-200'
        }`} data-testid="autoblog-banner">
          <span>{resultBanner.msg}</span>
          <button onClick={() => setResultBanner(null)} className="text-current opacity-60 hover:opacity-100 ml-3">×</button>
        </div>
      )}

      {/* Top status row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wider">Pending Drafts</p>
          <p className="text-2xl font-bold mt-1 text-amber-600">{drafts.length}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wider">Topics in Queue</p>
          <p className="text-2xl font-bold mt-1 text-blue-600">{queue?.available ?? 0} / {queue?.total_topics ?? 0}</p>
          <p className="text-[10px] text-gray-400 mt-1">{queue?.consumed ?? 0} consumed</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wider">Orphan URLs</p>
          <p className="text-2xl font-bold mt-1 text-pink-600">{orphans.length}</p>
          <p className="text-[10px] text-gray-400 mt-1">Auto-prioritized in next draft</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-4 flex items-center justify-center">
          <button onClick={runNow} disabled={running}
            className="px-4 py-2.5 bg-gray-900 text-white text-xs font-semibold rounded-lg hover:bg-black disabled:opacity-50 flex items-center gap-2"
            data-testid="autoblog-run-btn">
            {running ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
            {running ? 'Generating…' : 'Generate Now'}
          </button>
        </div>
      </div>

      {/* FAQ backfill panel */}
      <div className="bg-white rounded-xl border border-gray-100 p-4 flex flex-col md:flex-row md:items-center justify-between gap-3" data-testid="faq-backfill-panel">
        <div>
          <p className="text-xs text-gray-500 uppercase tracking-wider">AI FAQ Backfill</p>
          {faqStatus?.running ? (
            <p className="text-sm font-semibold text-blue-700 mt-1" data-testid="faq-backfill-progress">
              Running… {faqStatus.processed}/{faqStatus.total} articles
              {faqStatus.failed?.length > 0 && ` (${faqStatus.failed.length} failed)`}
            </p>
          ) : faqStatus?.finished_at ? (
            <p className="text-sm text-gray-700 mt-1" data-testid="faq-backfill-result">
              Last run: {faqStatus.updated_count} articles updated
              {faqStatus.failed?.length > 0 && `, ${faqStatus.failed.length} failed`}
              {faqStatus.indexnow?.ok != null && ` · IndexNow: ${faqStatus.indexnow.ok ? '✓' : '✗'}`}
            </p>
          ) : (
            <p className="text-sm text-gray-500 mt-1">Appends an SEO-optimized FAQ section (and FAQPage schema) to every published article that lacks one.</p>
          )}
        </div>
        <button onClick={startFaqBackfill} disabled={!!faqStatus?.running}
          className="px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold rounded-lg disabled:opacity-50 flex items-center gap-2 shrink-0"
          data-testid="faq-backfill-btn">
          {faqStatus?.running ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
          {faqStatus?.running ? 'Backfilling…' : 'Backfill FAQs'}
        </button>
      </div>

      {/* Research citations backfill panel */}
      <div className="bg-white rounded-xl border border-gray-100 p-4 flex flex-col md:flex-row md:items-center justify-between gap-3" data-testid="citations-backfill-panel">
        <div>
          <p className="text-xs text-gray-500 uppercase tracking-wider">Research Citations (PubMed)</p>
          {citeStatus?.running ? (
            <p className="text-sm font-semibold text-blue-700 mt-1" data-testid="citations-backfill-progress">
              Running… {citeStatus.processed}/{citeStatus.total} articles · {citeStatus.with_citations} with citations
              {citeStatus.failed?.length > 0 && ` (${citeStatus.failed.length} failed)`}
            </p>
          ) : citeStatus?.finished_at ? (
            <p className="text-sm text-gray-700 mt-1" data-testid="citations-backfill-result">
              Last run: {citeStatus.with_citations}/{citeStatus.processed} articles got verified citations
              {citeStatus.failed?.length > 0 && `, ${citeStatus.failed.length} failed`}
            </p>
          ) : (
            <p className="text-sm text-gray-500 mt-1">Attributes the real PubMed studies (and study authors) behind each science article. Every citation is verified against the live PubMed API.</p>
          )}
        </div>
        <button onClick={startCitationsBackfill} disabled={!!citeStatus?.running}
          className="px-4 py-2.5 bg-teal-600 hover:bg-teal-700 text-white text-xs font-semibold rounded-lg disabled:opacity-50 flex items-center gap-2 shrink-0"
          data-testid="citations-backfill-btn">
          {citeStatus?.running ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
          {citeStatus?.running ? 'Attributing…' : 'Attribute Sources'}
        </button>
      </div>

      {/* Drafts list */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wider flex items-center gap-2">
            <FileText className="w-4 h-4" /> Pending Drafts ({drafts.length})
          </h3>
          {drafts.length > 0 && (
            <div className="flex items-center gap-2">
              <button onClick={approveAll} disabled={!!actioning}
                className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-semibold rounded-lg flex items-center gap-1.5 disabled:opacity-50"
                data-testid="autoblog-approve-all-btn">
                {actioning === 'approve-all' ? <RefreshCw className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                Approve All ({drafts.length})
              </button>
              <button onClick={rejectAll} disabled={!!actioning}
                className="px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-700 text-[11px] font-semibold rounded-lg flex items-center gap-1.5 disabled:opacity-50"
                data-testid="autoblog-reject-all-btn">
                <Trash2 className="w-3 h-3" />
                Reject All
              </button>
            </div>
          )}
        </div>
        {drafts.length === 0 ? (
          <div className="text-center py-12 text-gray-400 bg-white rounded-xl border border-gray-100" data-testid="autoblog-empty">
            No pending drafts. Click <b>Generate Now</b> or wait for the daily 06:00 UTC run.
          </div>
        ) : (
          <div className="space-y-3">
            {drafts.map(d => {
              const wordCount = Math.round((d.content || '').replace(/<[^>]*>/g, ' ').split(/\s+/).filter(Boolean).length);
              const linkCount = ((d.content || '').match(/<a href="\//g) || []).length;
              const aprCount = ((d.content || '').match(/americanpeptideresearch\.net/g) || []).length;
              return (
                <div key={d.slug} className="bg-white rounded-xl border border-gray-100 p-4" data-testid={`autoblog-draft-${d.slug}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                        <span className="text-[10px] bg-purple-50 text-purple-700 px-1.5 py-0.5 rounded font-semibold uppercase">{d.category}</span>
                        <span className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded font-mono">{d.auto_draft_source || 'queue'}</span>
                        <span className="text-[10px] text-gray-400">{new Date(d.created_at).toLocaleString()}</span>
                      </div>
                      <h4 className="text-sm font-bold text-gray-900 leading-snug">{d.title}</h4>
                      <p className="text-xs text-gray-500 mt-1 line-clamp-2">{d.excerpt || d.meta_description}</p>
                      <div className="flex items-center gap-3 mt-2 text-[10px] text-gray-500">
                        <span>📝 {wordCount} words</span>
                        <span>⏱ {d.read_time} min read</span>
                        <span>❓ {(d.faqs || []).length} FAQs</span>
                        <span>📚 {(d.sources || []).length} sources</span>
                        <span className={linkCount >= 3 ? 'text-emerald-600 font-semibold' : 'text-amber-600'}>🔗 {linkCount} internal</span>
                        <span className={aprCount >= 1 ? 'text-emerald-600 font-semibold' : 'text-amber-600'}>💰 {aprCount} affiliate</span>
                      </div>
                    </div>
                    <div className="flex flex-col gap-1.5 flex-shrink-0">
                      <button onClick={() => setPreview(d)}
                        className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-[10px] font-semibold rounded-lg flex items-center gap-1"
                        data-testid={`autoblog-preview-${d.slug}`}>
                        <Eye className="w-3 h-3" /> Preview
                      </button>
                      <button onClick={() => approve(d.slug)} disabled={!!actioning}
                        className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] font-semibold rounded-lg flex items-center gap-1 disabled:opacity-50"
                        data-testid={`autoblog-approve-${d.slug}`}>
                        {actioning === `approve-${d.slug}` ? <RefreshCw className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                        Approve & Publish
                      </button>
                      <button onClick={() => reject(d.slug)} disabled={!!actioning}
                        className="px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-700 text-[10px] font-semibold rounded-lg flex items-center gap-1 disabled:opacity-50"
                        data-testid={`autoblog-reject-${d.slug}`}>
                        <Trash2 className="w-3 h-3" /> Reject
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Queue preview */}
      <div>
        <h3 className="text-sm font-bold text-gray-800 mb-3 uppercase tracking-wider flex items-center gap-2">
          <Calendar className="w-4 h-4" /> Upcoming Topics
        </h3>
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          {(queue?.next_5_available || []).length === 0 ? (
            <p className="p-4 text-xs text-gray-400">Queue exhausted — next runs will use AI-discovery to propose new angles.</p>
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-gray-50">
                <tr className="text-left text-gray-500 uppercase">
                  <th className="px-4 py-2">Vertical</th>
                  <th className="px-4 py-2">Title Hint</th>
                  <th className="px-4 py-2">Primary Keyword</th>
                </tr>
              </thead>
              <tbody>
                {queue.next_5_available.map((t, i) => (
                  <tr key={t.title_hint} className="border-t border-gray-50">
                    <td className="px-4 py-2"><span className="text-[10px] bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded font-semibold uppercase">{t.vertical}</span></td>
                    <td className="px-4 py-2 font-medium text-gray-700">{t.title_hint}</td>
                    <td className="px-4 py-2 text-gray-500">{t.primary_keyword}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Orphans diagnostics */}
      <div>
        <h3 className="text-sm font-bold text-gray-800 mb-3 uppercase tracking-wider flex items-center gap-2">
          <Link2 className="w-4 h-4" /> Orphan URLs (will be linked in next draft)
        </h3>
        <div className="bg-white rounded-xl border border-gray-100 p-3 max-h-60 overflow-auto">
          {orphans.length === 0 ? (
            <p className="text-xs text-gray-400">No orphans detected.</p>
          ) : (
            <ul className="space-y-1">
              {orphans.map(o => (
                <li key={o.url} className="text-[11px] flex items-center justify-between border-b border-gray-50 py-1">
                  <a href={o.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-mono">{o.url}</a>
                  <span className="text-gray-400">{o.inbound} inbound</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Preview modal */}
      {preview && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setPreview(null)}>
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-testid="autoblog-preview-modal">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <div>
                <p className="text-[10px] uppercase text-gray-500 font-semibold tracking-wide">Draft Preview</p>
                <h3 className="text-base font-bold text-gray-900">{preview.title}</h3>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => approve(preview.slug)} disabled={!!actioning}
                  className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] font-semibold rounded-lg flex items-center gap-1 disabled:opacity-50">
                  <CheckCircle2 className="w-3 h-3" /> Approve & Publish
                </button>
                <button onClick={() => setPreview(null)} className="text-gray-500 hover:text-black">
                  <XCircle className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="px-5 py-4 overflow-auto flex-1">
              <div className="text-xs text-gray-500 mb-3 flex flex-wrap gap-3">
                <span><b>Slug:</b> /blogs/article/{preview.slug}</span>
                <span><b>Meta title:</b> {preview.meta_title}</span>
              </div>
              <div className="prose prose-sm max-w-none text-gray-800" dangerouslySetInnerHTML={{ __html: preview.content || '' }} />
              {(preview.faqs || []).length > 0 && (
                <div className="mt-6 border-t border-gray-100 pt-4">
                  <h4 className="text-sm font-bold mb-2">FAQs</h4>
                  {(preview.faqs || []).map((f, i) => (
                    <details key={f.question} className="border border-gray-100 rounded-lg my-1 px-3 py-2 text-xs">
                      <summary className="font-semibold cursor-pointer">{f.question}</summary>
                      <p className="text-gray-600 mt-1">{f.answer}</p>
                    </details>
                  ))}
                </div>
              )}
              {(preview.sources || []).length > 0 && (
                <div className="mt-6 border-t border-gray-100 pt-4">
                  <h4 className="text-sm font-bold mb-2">Sources cited</h4>
                  <ol className="list-decimal list-inside text-xs text-gray-600 space-y-1">
                    {(preview.sources || []).map((s, i) => (
                      <li key={s.label}><a href={s.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{s.label}</a></li>
                    ))}
                  </ol>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
